import React, { useState } from "react";
import "./style.css";
import distanceIcon from "../../assets/Distanceicon.png";
import timeIcon from "../../assets/timeicon.png";
import Toggle from "./Toggle";

interface Activity {
  distanceKm: number;
  durationSec: number;
  percentage: number;
}

interface ActivityAreaProps {
  activity: Activity;
  handleOpenEvModal: () => void;
  handleToggle: () => void; // renamed to match parent

  /** Optional target distance for the challenge (in KM). */
  targetKm?: number;

  /**
   * "legacy" keeps the existing UI (left + button + toggles).
   * "dock" renders a clean stats card for the new map bottom dock.
   */
  variant?: "legacy" | "dock";
}

export default function ActivityArea({
  activity,
  handleOpenEvModal,
  handleToggle, // “Show Others” handler
  targetKm,
  variant = "legacy",
}: ActivityAreaProps) {
  // variant controls whether we show the legacy left column (+ / toggles)

  // Local state for KM ↔ MI
  const [unit, setUnit] = useState<"km" | "mi">("km");
  function toggleUnit() {
    setUnit((prev) => (prev === "km" ? "mi" : "km"));
  }

function secondsToHMS(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);

  return [h, m]
    .map((v, i) =>
      // pad to 2 digits, then add "h" if index 0, or "m" if index 1
      v.toString().padStart(2, "0") + (i === 0 ? "h" : "m")
    )
    .join(":");
}

  // Clamp percentage between 0 and 100
  const pct = Math.max(0, Math.min(100, activity.percentage));
  const fillStyle = { width: `${pct}%` };

  // Distance text based on selected unit
  const distanceText =
    unit === "km"
      ? `${activity.distanceKm.toFixed(1)} KM`
      : `${(activity.distanceKm * 0.621371).toFixed(2)} MI`;

  const targetText =
    typeof targetKm === "number"
      ? unit === "km"
        ? `Goal: ${targetKm.toFixed(0)} KM`
        : `Goal: ${(targetKm * 0.621371).toFixed(0)} MI`
      : null;

  return (
    <div className="ac-activity-wrapper">
      {variant === "legacy" && (
        <div className="ac-left-column">
          {/* “+” button */}
          <div
            className="ac-add-evidence-circle"
            onClick={handleOpenEvModal}
            title="Add Evidence"
          >
            +
          </div>

          <div className="ac-toggle-column">
            {/* Show Others toggle */}
            <div className="user-switch-small">
              <Toggle handleToggle={handleToggle} />
            </div>

            {/* KM/MI toggle */}
            <div className="distance-switch-small">
              <Toggle handleToggle={toggleUnit} />
            </div>
          </div>
        </div>
      )}

      {/* ── RIGHT COLUMN: percentage row (68px) above lower row (68px) ── */}
      <div className={variant === "dock" ? "ac-right-column ac-right-column--dock" : "ac-right-column"}>
        {/* Percentage row */}
        <div className="ac-percentage-row">
          <div className="ac-percentage-text">{pct}%</div>
          <div className="ac-progress-bar-inline">
            <div className="ac-progress-fill" style={fillStyle} />
          </div>
        </div>

        {/* Lower row: Distance and Time only */}
        <div className="ac-lower-row">
          <div
            className="ac-info-card-inline-large"
            onClick={toggleUnit}
            role="button"
            tabIndex={0}
            title="Tap to switch KM / MI"
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") toggleUnit();
            }}
          >
            <img
              className="ac-card-icon-small"
              src={distanceIcon}
              alt="Distance Icon"
            />
            <div className="ac-card-text-small">
              {distanceText}
              {variant === "dock" && targetText ? (
                <div className="ac-subtext">{targetText}</div>
              ) : null}
            </div>
          </div>

          <div className="ac-info-card-inline-large">
            <img
              className="ac-card-icon-small"
              src={timeIcon}
              alt="Time Icon"
            />
            <div className="ac-card-text-small">
              {secondsToHMS(activity.durationSec)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
